import { getApiClient } from 'saletrax-shared/clients/apiClient';

/**
 * Get marketplace configuration from the backend API.
 *
 * Resolution order:
 *  1) If running in a real browser on a real custom domain, look up by hostname
 *  2) Otherwise fall back to the build-time env var (NEXT_PUBLIC_MARKETPLACE_CONFIG / VITE_MARKETPLACE_CONFIG)
 *
 * The hostname-based path is what makes a single Marketplace deployment serve many vendors.
 */
export async function getMarketplaceConfig(): Promise<MarketplaceConfig | null> {
  const api = getApiClient();

  // 1. Hostname-based resolution (production custom domains)
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const host = window.location.hostname.toLowerCase();
    const isLocalOrPlatform =
      host === 'localhost' ||
      host.endsWith('.ondigitalocean.app') ||
      host.endsWith('.vercel.app') ||
      host.endsWith('.netlify.app') ||
      /^\d+\.\d+\.\d+\.\d+$/.test(host);
    if (!isLocalOrPlatform) {
      try {
        const res = await api.get('/marketplace-configs/by-domain', { params: { hostname: host } });
        const data = res.data?.data;
        if (data) {
          return { ...data, id: data._id || data.id } as MarketplaceConfig;
        }
      } catch {
        // fall through to env-var fallback
      }
    }
  }

  // 2. Env-var fallback (dev, preview, single-tenant deploys)
  try {
    const docId = process.env.NEXT_PUBLIC_MARKETPLACE_CONFIG
      || (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_MARKETPLACE_CONFIG);
    if (!docId) {
      return null;
    }
    const res = await api.get(`/marketplace-configs/public/${docId}`);
    const data = res.data.data;
    return { ...data, id: data._id || data.id } as MarketplaceConfig;
  } catch {
    return null;
  }
}

/**
 * Get marketplace configuration synchronously (for client components)
 * This uses a cached version or returns null if not available
 */
let cachedConfig: MarketplaceConfig | null = null;
let configPromise: Promise<MarketplaceConfig | null> | null = null;

export function getMarketplaceConfigSync(): MarketplaceConfig | null {
  return cachedConfig;
}

export function loadMarketplaceConfig(): Promise<MarketplaceConfig | null> {
  if (configPromise) {
    return configPromise;
  }
  configPromise = getMarketplaceConfig().then(config => {
    cachedConfig = config;
    return config;
  });
  return configPromise;
}

export interface MarketplaceConfig {
  id?: string;
  organizationId?: string;
  marketplaceId?: string;
  distributorId?: string;
  name?: string;
  logo?: string;
  logoText?: string;
  tagline?: string;
  domain?: string;
  themeColor?: string;
  transparentTexturesConfig?: {
    pattern?: string;
    color?: string;
  };
  contactEmail?: string;
  contactPhone?: string;
  address?: {
    address1?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
  active?: boolean;
  public?: boolean;
  headerBarText?: string;
  marketplaceCatalog?: string;
  // Feature flags
  showRecipes?: boolean;
  showDeliverySchedule?: boolean;
  showBuyAgain?: boolean;
  showReturns?: boolean;
  showSupport?: boolean;
  showChat?: boolean;
  // Marketing
  heroHeading?: string;
  heroSubheading?: string;
  featuredCategories?: string[];
}
